// app/components/CookiesPolicy.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, List, ListItem } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function CookiesPolicy() {
  const t = useTranslations("CookiesPolicy.Content");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // แปลง pathname → path ที่ใช้ใน CMS (ตัด /en /zh ด้านหน้า)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ----- ค่า default จาก i18n (ดีต่อ SEO/SSR) -----
  const [p1, setP1] = React.useState(t("p1"));
  const [whatTitle, setWhatTitle] = React.useState(t("whatAreCookiesTitle"));
  const [whatContent1, setWhatContent1] = React.useState(
    t("whatAreCookiesContent1")
  );
  const [whatContent2, setWhatContent2] = React.useState(
    t("whatAreCookiesContent2")
  );
  const [howTitle, setHowTitle] = React.useState(t("howWeUseCookiesTitle"));
  const [howContent, setHowContent] = React.useState(
    t("howWeUseCookiesContent")
  );
  const [thirdParty, setThirdParty] = React.useState(t("thirdPartyCookies"));
  const [cookieSettings, setCookieSettings] = React.useState(
    t("cookieSettingsContent")
  );

  // list เป็น array
  const [whatList, setWhatList] = React.useState<string[]>(
    t.raw("whatAreCookiesList") as string[]
  );

  // locale เปลี่ยน → reset กลับค่า default จาก i18n
  React.useEffect(() => {
    setP1(t("p1"));
    setWhatTitle(t("whatAreCookiesTitle"));
    setWhatContent1(t("whatAreCookiesContent1"));
    setWhatContent2(t("whatAreCookiesContent2"));
    setHowTitle(t("howWeUseCookiesTitle"));
    setHowContent(t("howWeUseCookiesContent"));
    setThirdParty(t("thirdPartyCookies"));
    setCookieSettings(t("cookieSettingsContent"));
    setWhatList(t.raw("whatAreCookiesList") as string[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);


  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        // override ค่า ตาม cmsKey ที่เรากำหนด
        if (map["cookies.p1"]) setP1(map["cookies.p1"]);

        if (map["cookies.whatAreCookiesTitle"])
          setWhatTitle(map["cookies.whatAreCookiesTitle"]);
        if (map["cookies.whatAreCookiesContent1"])
          setWhatContent1(map["cookies.whatAreCookiesContent1"]);
        if (map["cookies.whatAreCookiesContent2"])
          setWhatContent2(map["cookies.whatAreCookiesContent2"]);

        // list เป็น array key: cookies.whatAreCookiesList.0, 1, ...
        setWhatList((prev) => {
          const next = [...prev];
          for (let i = 0; i < next.length; i++) {
            const k = `cookies.whatAreCookiesList.${i}`;
            if (map[k]) next[i] = map[k];
          }
          return next;
        });

        if (map["cookies.howWeUseCookiesTitle"])
          setHowTitle(map["cookies.howWeUseCookiesTitle"]);
        if (map["cookies.howWeUseCookiesContent"])
          setHowContent(map["cookies.howWeUseCookiesContent"]);

        if (map["cookies.thirdPartyCookies"])
          setThirdParty(map["cookies.thirdPartyCookies"]);
        if (map["cookies.cookieSettingsContent"])
          setCookieSettings(map["cookies.cookieSettingsContent"]);
      } catch (err) {
        console.warn("[CookiesPolicy] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box className="policy-wrap" sx={{
      py: 10
    }}>
      <Container className="container">
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="cookies.p1"
          sx={{
            mb: 3
          }}
        >
          {p1}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="cookies.whatAreCookiesTitle"
          sx={{
            mb: 3
          }}
        >
          {whatTitle}
        </Typography>

        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="cookies.whatAreCookiesContent1"
          sx={{
            mb: 3
          }}
        >
          {whatContent1}
        </Typography>

        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="cookies.whatAreCookiesContent2"
          sx={{
            mb: 3
          }}
        >
          {whatContent2}
        </Typography>

        <List sx={{ mb: 3, pl: "0 !important" }}>
          {whatList.map((item, index) => (
            <ListItem
              key={index}
              sx={{ display: "list-item", pl: 0 }}
            >
              <Typography
                variant="body1"
                data-aos="fade-up"
                data-cms-key={`cookies.whatAreCookiesList.${index}`}
              >
                {item}
              </Typography>
            </ListItem>
          ))}
        </List>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="cookies.howWeUseCookiesTitle"
          sx={{
            mb: 3
          }}
        >
          {howTitle}
        </Typography>

        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="cookies.howWeUseCookiesContent"
          sx={{
            mb: 3
          }}
        >
          {howContent}
        </Typography>

        {/* ตรง strictlyNecessaryCookies / analyticCookies ยังใช้ HTML ตรงจาก i18n (ไม่ผูก inline เพื่อไม่เสีย markup) */}
        <Typography
          variant="body1"
          data-aos="fade-up"
          dangerouslySetInnerHTML={{
            __html: t.raw("strictlyNecessaryCookies") as string,
          }}
          sx={{
            mb: 3
          }}
        />
        <Typography
          variant="body1"
          data-aos="fade-up"
          dangerouslySetInnerHTML={{
            __html: t.raw("analyticCookies") as string,
          }}
          sx={{
            mb: 3
          }}
        />

        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="cookies.thirdPartyCookies"
          sx={{
            mb: 3
          }}
        >
          {thirdParty}
        </Typography>

        {/* heading ซ้ำ → ใช้ข้อความเดียวกับด้านบน (cmsKey เดียวกัน) */}
        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="cookies.howWeUseCookiesTitle"
          sx={{
            mb: 3
          }}
        >
          {howTitle}
        </Typography>

        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="cookies.cookieSettingsContent"
          sx={{
            mb: 3
          }}
        >
          {cookieSettings}
        </Typography>
      </Container>
    </Box>
  );
}